/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement, useEffect, useRef, useState } from "react"
import { useField } from "formik"
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/outline"
import { classNames } from "@medaica/common/services/util"

export type SelectOption = { value: string; label: string }

/**
 * Custom single-select dropdown (replaces the native `<select>` on the Patient step). Writes the
 * SAME option `value` the native select did (e.g. "male" / "female" / "unknown"), so submission is
 * unchanged — it only restyles the control.
 */
const GenderSelect = ({
  name,
  label,
  placeholder,
  options,
  className,
}: {
  name: string
  label: string
  placeholder: string
  options: SelectOption[]
  // Extra classes for the trigger. Buttons don't receive @tailwindcss/forms' input padding
  // (it targets input/select elements), so the caller passes the skin that keeps this
  // trigger pixel-identical to the neighboring text inputs.
  className?: string
}): ReactElement => {
  const [field, meta, helpers] = useField<string>(name)
  const showError = meta.touched && !!meta.error
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const selected = options.find((option) => option.value === field.value)

  const close = (): void => {
    setOpen(false)
    void helpers.setTouched(true)
  }

  useEffect(() => {
    if (!open) {
      return
    }
    const onPointerDown = (event: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        close()
      }
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        close()
        triggerRef.current?.focus() // Escape returns focus to the trigger (WCAG 2.4.3)
      }
    }
    document.addEventListener("mousedown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("mousedown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const choose = (value: string): void => {
    // Mark touched WITHOUT re-validating, then validate on setValue — otherwise Formik validates
    // against the pre-commit (empty) value and flashes a stale "Required".
    void helpers.setTouched(true, false)
    void helpers.setValue(value, true)
    setOpen(false)
    triggerRef.current?.focus() // return focus to the trigger so it isn't dropped to <body> on close
  }

  return (
    <div ref={containerRef} className="relative">
      <label className="block font-base text-gray-700">{label}</label>
      <div className="mt-1">
        <button
          ref={triggerRef}
          type="button"
          onClick={() => (open ? close() : setOpen(true))}
          className={classNames(
            "input flex w-full items-center justify-between text-left",
            className,
            showError ? "input-error" : ""
          )}
          aria-expanded={open}
          aria-describedby={showError ? `${name}-error` : undefined}
        >
          <span className={selected ? "text-gray-900" : "text-gray-500"}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronDownIcon className="h-5 w-5 flex-shrink-0 text-gray-500" aria-hidden="true" />
        </button>
      </div>

      {open && (
        <div className="absolute z-20 mt-1 w-full rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
          {options.map((option) => {
            const active = option.value === field.value
            return (
              <button
                key={option.value}
                type="button"
                aria-pressed={active}
                onClick={() => choose(option.value)}
                className={classNames(
                  "flex w-full items-center justify-between px-3 py-2 text-left text-sm",
                  active ? "bg-primary-100 font-medium text-primary" : "text-gray-800 hover:bg-gray-50"
                )}
              >
                {option.label}
                {active && <CheckIcon className="h-4 w-4" aria-hidden="true" />}
              </button>
            )
          })}
        </div>
      )}

      {showError && (
        <div id={`${name}-error`} role="alert" className="mt-1 text-error">
          {meta.error}
        </div>
      )}
    </div>
  )
}

export default GenderSelect
